import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
} from 'react-router-dom';
import { UserWithMetadataDtoRoleEnum } from '@kortxio/hub-api';
import * as Sentry from '@sentry/react';
import { WithAdminCampaigns } from 'features/admin-campaigns/components/WithAdminCampaigns/WithAdminCampaigns';
import RequireAccessToAdvertiser from 'features/advertiser/components/RequireAccessToAdvertiser';
import WithSelectedAdvertiser from 'features/advertiser/components/WithSelectedAdvertiser';
import AuthLayout from 'features/auth/AuthLayout';
import { loginRedirectUrl } from 'features/auth/functions';
import RequireAuth from 'features/auth/RequireAuth';
import ErrorBackdrop from 'features/error/components/ErrorBackdrop';
import KampusContextProvider from 'features/kampus/context/KampusContextProvider';
import KampusUserProvider from 'features/kampus/KampusUserProvider';
import RequirePrivilege from 'features/user/components/RequirePrivilege';
import RequireRole from 'features/user/components/RequireRole';
import RequireTermsOfUseAccepted from 'features/user/components/RequireTermsOfUseAccepted';
import { AdminDrawerListOptions } from 'layout/components/drawer/AdminDrawerListOptions';
import { StandardDrawerListOptions } from 'layout/components/drawer/StandardDrawerListOptions';
import Layout from 'layout/index';
import config from 'libs/config';
import DomainAuthLayout from 'libs/domain/wrappers/DomainAuthLayout';
import { base64ToUrl, urlToBase64 } from 'libs/string';
import ForgotPassword from 'pages/auth/forgot-password';
import Login from 'pages/auth/login';
import NewPassword from 'pages/auth/new-password';
import ClientSelectPage from 'pages/client-select';
import DemoModeController from 'pages/demo-mode-controller';
import Error from 'pages/error';
import IndexPage from 'pages/index';
import NotFound from 'pages/not-found';
import TermsOfUse from 'pages/terms-of-use';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { lazyRouteFunction } from './helper';

const routes = [
  {
    errorElement: <Error />,
    element: (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <KampusUserProvider>
          <KampusContextProvider>
            <ErrorBackdrop />
            <Outlet />
          </KampusContextProvider>
        </KampusUserProvider>
      </QueryParamProvider>
    ),
    children: [
      {
        path: '/auth',
        element: (
          <RequireAuth
            inverse
            redirectPath={(hubUser) => {
              return hubUser
                ? loginRedirectUrl(
                    hubUser,
                    base64ToUrl(window.location.hash.substring(1))
                  )
                : '/';
            }}
          >
            <DomainAuthLayout domainConfig={config.domain}>
              <Outlet />
            </DomainAuthLayout>
          </RequireAuth>
        ),
        children: [
          {
            path: 'login',
            element: <Login />,
          },
          {
            path: 'new-password',
            element: <NewPassword />,
          },
          {
            path: 'forgot-password',
            element: <ForgotPassword />,
          },
        ],
      },
      {
        path: 'client-select',
        // Regular AuthLayout is used here, client-select is a special case.
        element: (
          <RequireAuth>
            <AuthLayout>
              <ClientSelectPage />
            </AuthLayout>
          </RequireAuth>
        ),
      },
      {
        path: 'demo-mode-controller',
        element: (
          <RequireAuth>
            <DemoModeController />
          </RequireAuth>
        ),
      },
      {
        path: '/admin',
        element: (
          <RequireAuth>
            <RequireRole
              redirectTo="/"
              requiredRoles={[
                UserWithMetadataDtoRoleEnum.KortxAdmin,
                UserWithMetadataDtoRoleEnum.KortxStandard,
                UserWithMetadataDtoRoleEnum.PartnerSolutions,
              ]}
            >
              <Layout drawerOptions={<AdminDrawerListOptions />} />
            </RequireRole>
          </RequireAuth>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="persona-management" replace />,
          },
          {
            path: 'user-management',
            lazy: lazyRouteFunction(
              () => import('pages/admin/user-management')
            ),
          },
          {
            path: 'persona-management',
            children: [
              {
                index: true,
                lazy: lazyRouteFunction(
                  () => import('pages/admin/persona-management')
                ),
              },
              {
                path: 'add',
                lazy: lazyRouteFunction(
                  () => import('pages/admin/persona-management/add')
                ),
              },
              {
                path: ':personaId',
                lazy: lazyRouteFunction(
                  () => import('pages/admin/persona-management/[personaId]')
                ),
              },
            ],
          },
          {
            path: 'prompt-management',
            children: [
              {
                index: true,
                lazy: lazyRouteFunction(
                  () => import('pages/admin/prompt-management')
                ),
              },
              {
                path: ':promptConfigId',
                lazy: lazyRouteFunction(
                  () => import('pages/admin/prompt-management/[promptConfigId]')
                ),
              },
            ],
          },
          {
            path: 'ai-responses',
            lazy: lazyRouteFunction(() => import('pages/admin/ai-responses')),
          },
          {
            path: 'monitoring',
            element: (
              <RequirePrivilege
                requiredPrivileges={['MONITORS_READ']}
                redirectTo="/admin"
              >
                <Outlet />
              </RequirePrivilege>
            ),
            children: [
              {
                lazy: lazyRouteFunction(() => import('pages/admin/monitoring')),
                children: [
                  {
                    index: true,
                    element: <Navigate to="command-center" replace />,
                  },
                  {
                    path: 'command-center',
                    lazy: lazyRouteFunction(
                      () => import('pages/admin/monitoring/CommandCenter')
                    ),
                  },
                  {
                    path: 'violations',
                    lazy: lazyRouteFunction(
                      () => import('pages/admin/monitoring/MonitorViolations')
                    ),
                  },
                  {
                    path: 'monitors',
                    lazy: lazyRouteFunction(
                      () => import('pages/admin/monitoring/Monitors')
                    ),
                  },
                  {
                    path: 'monitors/:monitorKey/config',
                    lazy: lazyRouteFunction(
                      () => import('pages/admin/monitoring/MonitorConfig')
                    ),
                  },
                  {
                    path: 'run-history',
                    lazy: lazyRouteFunction(
                      () => import('pages/admin/monitoring/RunHistory')
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'campaigns',
            element: (
              <WithAdminCampaigns>
                <Outlet />
              </WithAdminCampaigns>
            ),
            children: [
              {
                index: true,
                lazy: lazyRouteFunction(() => import('pages/admin/campaigns')),
              },
              {
                path: 'bulk-update',
                lazy: lazyRouteFunction(
                  () => import('pages/admin/campaigns/bulk-update')
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/terms-of-use',
        element: (
          <RequireAuth>
            <Layout drawerOptions={<StandardDrawerListOptions />} />
            <TermsOfUse />
          </RequireAuth>
        ),
      },
      {
        // OAuth redirects to this page. The page itself isn't rendered, as we
        // are interested in the query params.
        // Should match VITE_OAUTH_REDIRECT_URL.
        path: '/oauth-redirect',
        element: <Outlet />,
      },
      {
        path: '/',
        element: (
          <RequireAuth
            redirectPath={() => {
              const { pathname } = window.location;
              // If the route is invalid (e.g. matchRoutes returns null), we
              // will serve a 404 page. no need to match Path here.
              // Individual pages need to handle 404 for resources however.
              if (pathname !== '/') {
                return '/auth/login#' + urlToBase64(pathname);
              } else {
                return 'auth/login';
              }
            }}
          >
            <RequireTermsOfUseAccepted>
              <Layout drawerOptions={<StandardDrawerListOptions />} />
            </RequireTermsOfUseAccepted>
          </RequireAuth>
        ),
        children: [
          {
            index: true,
            element: <IndexPage />,
          },
          {
            path: '/clients/:clientId',
            children: [
              {
                path: 'services',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/services')
                ),
                children: [
                  {
                    index: true,
                    lazy: lazyRouteFunction(
                      () => import('pages/clients/[clientId]/services/all')
                    ),
                  },
                  {
                    path: 'all',
                    lazy: lazyRouteFunction(
                      () => import('pages/clients/[clientId]/services/all')
                    ),
                  },
                  {
                    path: ':productCategory',
                    lazy: lazyRouteFunction(
                      () =>
                        import(
                          'pages/clients/[clientId]/services/[product-category]'
                        )
                    ),
                  },
                ],
              },
              {
                path: 'advertisers',
                children: [
                  {
                    index: true,
                    lazy: lazyRouteFunction(
                      () => import('pages/clients/[clientId]/advertisers')
                    ),
                  },
                  {
                    path: ':advertiserId',
                    lazy: lazyRouteFunction(
                      () =>
                        import(
                          'pages/clients/[clientId]/advertisers/[advertiserId]'
                        )
                    ),
                    children: [
                      {
                        path: ':activeTab',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/advertisers/[advertiserId]/[activeTab]'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'audiences',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/audiences')
                ),
                children: [
                  {
                    path: ':advertiserId',
                    element: (
                      <WithSelectedAdvertiser>
                        <RequireAccessToAdvertiser>
                          <Outlet />
                        </RequireAccessToAdvertiser>
                      </WithSelectedAdvertiser>
                    ),
                    children: [
                      {
                        path: 'segment-analysis',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/audiences/[advertiserId]/segment-analysis'
                            )
                        ),
                        children: [
                          {
                            path: ':audienceId/audience-composition',
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/audiences/[advertiserId]/segment-analysis/[audienceId]/audience-composition'
                                )
                            ),
                          },
                          {
                            path: ':audienceId/persona',
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/audiences/[advertiserId]/segment-analysis/[audienceId]/persona'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'taxonomy',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/audiences/[advertiserId]/taxonomy'
                            )
                        ),
                      },
                      {
                        path: 'crm',
                        children: [
                          {
                            index: true,
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/audiences/[advertiserId]/crm'
                                )
                            ),
                          },
                          {
                            path: ':crmAudienceId',
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/audiences/[advertiserId]/crm/[crmAudienceId]'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'campaigns',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    lazy: lazyRouteFunction(
                      () => import('pages/clients/[clientId]/campaigns')
                    ),
                  },
                  {
                    path: ':campaignId',
                    lazy: lazyRouteFunction(
                      () =>
                        import(
                          'pages/clients/[clientId]/campaigns/[campaignId]'
                        )
                    ),
                    children: [
                      {
                        path: ':activeTab',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/campaigns/[campaignId]/[activeTab]'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'reporting-suite',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/reporting-suite')
                ),
                children: [
                  {
                    path: 'all',
                    element: <Outlet />,
                    children: [
                      {
                        path: 'dashboards',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/all/dashboards'
                            )
                        ),
                      },
                      {
                        path: 'dashboards/custom-report',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/dashboards/custom-report'
                            )
                        ),
                      },
                      {
                        path: 'dashboards/reports/:reportName',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/all/dashboards/reports/[reportName]'
                            )
                        ),
                      },
                      {
                        path: 'campaign-reporting',
                        lazy: async () => {
                          return {
                            element: <Outlet />,
                          };
                        },
                        children: [
                          {
                            index: true,
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/reporting-suite/all/campaign-reporting'
                                )
                            ),
                          },
                          {
                            path: ':campaignId',
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/reporting-suite/all/campaign-reporting/[campaignId]'
                                )
                            ),
                            children: [
                              {
                                path: ':activeTab',
                                lazy: lazyRouteFunction(
                                  () =>
                                    import(
                                      'pages/clients/[clientId]/reporting-suite/all/campaign-reporting/[campaignId]/[activeTab]'
                                    )
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ':advertiserId',
                    element: (
                      <WithSelectedAdvertiser>
                        <RequireAccessToAdvertiser>
                          <Outlet />
                        </RequireAccessToAdvertiser>
                      </WithSelectedAdvertiser>
                    ),
                    children: [
                      {
                        path: 'dashboards',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/dashboards'
                            )
                        ),
                      },
                      {
                        path: 'dashboards/custom-report',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/dashboards/custom-report'
                            )
                        ),
                      },
                      {
                        path: 'dashboards/reports/:reportName',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/dashboards/reports/[reportName]'
                            )
                        ),
                      },
                      {
                        path: 'assistant/:campaignId?',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/assistant'
                            )
                        ),
                      },
                      {
                        path: 'campaign-reporting',
                        lazy: async () => {
                          return {
                            element: <Outlet />,
                          };
                        },
                        children: [
                          {
                            index: true,
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/reporting-suite/[advertiserId]/campaign-reporting'
                                )
                            ),
                          },
                          {
                            path: ':campaignId',
                            lazy: lazyRouteFunction(
                              () =>
                                import(
                                  'pages/clients/[clientId]/reporting-suite/all/campaign-reporting/[campaignId]'
                                )
                            ),
                            children: [
                              {
                                path: ':activeTab',
                                lazy: lazyRouteFunction(
                                  () =>
                                    import(
                                      'pages/clients/[clientId]/reporting-suite/all/campaign-reporting/[campaignId]/[activeTab]'
                                    )
                                ),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: 'builder',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/builder'
                            )
                        ),
                      },
                      {
                        path: 'builder/schedules',
                        lazy: lazyRouteFunction(
                          () =>
                            import(
                              'pages/clients/[clientId]/reporting-suite/[advertiserId]/builder/schedules'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'billing',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/billing')
                ),
              },
              {
                path: 'user-management',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/user-management')
                ),
              },
              {
                path: 'profile',
                lazy: lazyRouteFunction(
                  () => import('pages/clients/[clientId]/profile')
                ),
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = config.sentry.isEnabled
  ? sentryCreateBrowserRouter(routes)
  : createBrowserRouter(routes as RouteObject[]);
